import React from 'react';
import { useTranslation } from 'react-i18next';

import { IRenderOptionProps } from '@private/components';

import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useConfirmActionModal } from 'modals/ConfirmActionModal';
import { useDeleteFiltersPreset } from 'queries/portalUser';

import { REDIRECTED_PRESET } from '../helpers';

import { Container, Icons, RemoveButton, StyledCheck, Text } from './styled';

interface IPresetOption<T> extends IRenderOptionProps<T> {
  pageKey?: string;
}

const PresetOption = <T,>({
  disabled,
  highlighted,
  item: { customProps, label = '', value },
  pageKey,
  searchTerm,
  selected,
}: IPresetOption<T>) => {
  const { t } = useTranslation();

  const { isSystem } = (customProps as { isSystem: boolean }) || { isSystem: false };

  const { isPending: isDeletingPreset, mutateAsync: deletePreset } =
    useDeleteFiltersPreset();

  const [showConfirmModal] = useConfirmActionModal();

  const onDeletePreset = React.useCallback(async () => {
    if (pageKey) {
      await deletePreset({ pageKey, presetId: value as string });
    }
  }, [deletePreset, pageKey, value]);

  const confirmDeleteAction = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      showConfirmModal({
        cancelText: t('common.cancel'),
        confirmText: t('common.delete'),
        enableLoading: true,
        message: `${t('common.filters.confirmDelete')} "${label}"?`,
        onConfirm: onDeletePreset,
        rightBtnVariant: 'danger',
      });
    },
    [label, onDeletePreset, showConfirmModal, t],
  );

  return (
    <Container
      data-is-disabled={disabled}
      data-is-highlighted={highlighted}
      data-is-selected={selected}
      data-testid='presetOption'
    >
      <Text searchTerm={searchTerm} testIds={{ highlightedText: 'presetOptionName' }}>
        {label}
      </Text>
      <Icons>
        {selected && (
          <StyledCheck
            $isSingleIcon={!value || value === REDIRECTED_PRESET || isSystem}
            data-testid='presetOptionCheckIcon'
            width={15}
          />
        )}
        {value && value !== REDIRECTED_PRESET && !isSystem && (
          <RemoveButton
            disabled={isDeletingPreset}
            iconSize={14}
            isLoading={isDeletingPreset}
            loaderSize={20}
            onClick={confirmDeleteAction}
            size='sm'
            startIcon={<ICONS_MAP.Delete />}
            testIds={{ container: 'presetOptionRemoveButton' }}
            variant='icon'
          />
        )}
      </Icons>
    </Container>
  );
};

export default appReactMemo(PresetOption);
